/**
 * Polyline2DRenderer — Renders exposed polyline outputs on a 2D canvas.
 *
 * Collects polyline data from the definition's exposed endpoints (same
 * pattern as PerformanceRenderer collects three:group outputs for 3D).
 * No syncNodes — just collectPolylines() then render().
 */

import { BaseRenderer } from '@/plugins/video/BaseRenderer';
import type { ExposedEndpoint } from '@/graph/GraphDefinition';
import type { IGraphInstance } from '@/graph/IGraphInstance';
import type { GraphIndex } from '@/runtime/GraphIndex';
import type { Interaction2D } from '@/nodes/Interaction2D';
import type { Polylines } from '@/plugins/generative/TurtleNode/TurtleNode';
import { resolveConfigValue } from '@/runtime/util';
import { logger } from '@/logging';

const DEFAULT_COLOR = '#3b82f6';
const PADDING_RATIO = 0.08;

export class Polyline2DRenderer extends BaseRenderer {
  private ctx: CanvasRenderingContext2D | null = null;
  private width = 0;
  private height = 0;
  private polylines: Polylines = [];

  // Cached view transform
  private cachedScale = 1;
  private cachedCx = 0;
  private cachedCy = 0;
  private cachedPolylineCount = -1;

  private getInstance: (instanceId: string) => IGraphInstance | null;
  private graphIndex: GraphIndex;

  constructor(
    private instanceId: string,
    getInstance: (instanceId: string) => IGraphInstance | null,
    graphIndex: GraphIndex,
  ) {
    super();
    this.getInstance = getInstance;
    this.graphIndex = graphIndex;
  }

  protected initializeRenderer(canvas: HTMLCanvasElement): void {
    this.ctx = canvas.getContext('2d');
    this.cachedPolylineCount = -1;
  }

  setSize(w: number, h: number): void {
    this.width = w;
    this.height = h;
    const dpr = window.devicePixelRatio || 1;
    if (this.canvas) {
      this.canvas.width = w * dpr;
      this.canvas.height = h * dpr;
      this.canvas.style.width = `${w}px`;
      this.canvas.style.height = `${h}px`;
    }
    this.cachedPolylineCount = -1;
  }

  /**
   * Collect polyline data from exposed endpoints on the definition's API.
   * Call this per-frame before render().
   */
  private loggedOnce = false;

  collectPolylines(api: Record<string, ExposedEndpoint> | undefined): void {
    const all: Polylines = [];
    if (!api) {
      if (!this.loggedOnce) {
        logger.trace('Polyline2DRenderer', 'collectPolylines: no api');
        this.loggedOnce = true;
      }
      this.polylines = all;
      return;
    }

    const instance = this.getInstance(this.instanceId);
    if (!instance) {
      if (!this.loggedOnce) {
        logger.trace('Polyline2DRenderer', `collectPolylines: no instance for ${this.instanceId}`);
        this.loggedOnce = true;
      }
      this.polylines = all;
      return;
    }

    if (!this.loggedOnce) {
      const apiKeys = Object.keys(api);
      const epEntries = Object.entries(api).map(([k, e]) => `${k}(${e.dataType}, input=${e.isInput}, output=${e.isOutput}, path=${e.path})`);
      logger.trace('Polyline2DRenderer', `collectPolylines: instance=${this.instanceId}, api keys=[${apiKeys}], endpoints=[${epEntries}]`);
      const instanceEpKeys = Object.keys(instance.endpoints);
      logger.trace('Polyline2DRenderer', `instance.endpoints keys=[${instanceEpKeys}]`);
      this.loggedOnce = true;
    }

    for (const [, exposed] of Object.entries(api)) {
      // Only non-input polyline endpoints
      if (exposed.isInput && !exposed.isOutput) continue;
      if (exposed.dataType !== 'polylines') continue;

      // Read value from the endpoint: path is "nodeId.endpointId"
      const ep = instance.endpoints[exposed.path];
      if (!ep) continue;

      const payload = ep.getValue();
      const value = resolveConfigValue(payload);
      if (Array.isArray(value)) {
        for (const line of value) {
          if (line && Array.isArray(line.points)) {
            all.push(line);
          }
        }
      }
    }

    this.polylines = all;
  }

  protected _render(): void {
    const ctx = this.ctx;
    if (!ctx || !this.canvas) return;

    const dpr = window.devicePixelRatio || 1;
    const w = this.width * dpr;
    const h = this.height * dpr;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, w, h);

    if (this.polylines.length === 0) return;

    // Recompute view transform when polyline count changes
    if (this.polylines.length !== this.cachedPolylineCount) {
      this.cachedPolylineCount = this.polylines.length;

      let minX = Infinity, minY = Infinity;
      let maxX = -Infinity, maxY = -Infinity;
      for (const line of this.polylines) {
        for (const p of line.points) {
          if (p.x < minX) minX = p.x;
          if (p.x > maxX) maxX = p.x;
          if (p.y < minY) minY = p.y;
          if (p.y > maxY) maxY = p.y;
        }
      }

      const spanX = maxX - minX || 1;
      const spanY = maxY - minY || 1;
      this.cachedCx = (minX + maxX) / 2;
      this.cachedCy = (minY + maxY) / 2;

      const padding = Math.min(w, h) * PADDING_RATIO;
      const drawW = w - padding * 2;
      const drawH = h - padding * 2;
      this.cachedScale = Math.min(drawW / spanX, drawH / spanY);
    }

    const scale = this.cachedScale;
    const cx = this.cachedCx;
    const cy = this.cachedCy;
    const offsetX = w / 2;
    const offsetY = h / 2;

    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    for (const line of this.polylines) {
      if (line.points.length < 2) continue;
      ctx.beginPath();
      const p0 = line.points[0];
      ctx.moveTo(offsetX + (p0.x - cx) * scale, offsetY - (p0.y - cy) * scale);
      for (let i = 1; i < line.points.length; i++) {
        const p = line.points[i];
        ctx.lineTo(offsetX + (p.x - cx) * scale, offsetY - (p.y - cy) * scale);
      }
      if (line.fill) {
        ctx.closePath();
        ctx.fillStyle = line.fill;
        ctx.fill();
      }
      if (line.color || !line.fill) {
        ctx.strokeStyle = line.color ?? DEFAULT_COLOR;
        ctx.lineWidth = (line.lineWidth ?? 1.5) * Math.min(w, h) / 300;
        ctx.stroke();
      }
    }
  }

  // ─── Coordinate conversion ──────────────────────────────────

  /** Convert CSS coordinates to data-space coordinates (inverse of render transform). */
  canvasToData(cssX: number, cssY: number): { x: number; y: number } {
    const dpr = window.devicePixelRatio || 1;
    const w = this.width * dpr;
    const h = this.height * dpr;
    const offsetX = w / 2;
    const offsetY = h / 2;
    const scale = this.cachedScale;
    const cx = this.cachedCx;
    const cy = this.cachedCy;

    return {
      x: (cssX * dpr - offsetX) / scale + cx,
      y: (offsetY - cssY * dpr) / scale + cy,
    };
  }

  /** Current data→screen scale factor (for converting screen-px pick radius to data units). */
  getScale(): number {
    return this.cachedScale;
  }

  // ─── Interaction2D discovery ────────────────────────────────

  /**
   * Resolve all Interaction2D instances from exposed polyline endpoints.
   * Returns them in API order (draw order) — caller reverses for topmost-first dispatch.
   */
  resolveAllInteractions2D(api: Record<string, ExposedEndpoint>): Interaction2D[] {
    const interactions: Interaction2D[] = [];
    for (const [, ep] of Object.entries(api)) {
      if (ep.dataType !== 'polylines' || (ep.isInput && !ep.isOutput)) continue;

      const dotIdx = ep.path.lastIndexOf('.');
      if (dotIdx === -1) continue;
      const nodeId = ep.path.slice(0, dotIdx);
      const endpointId = ep.path.slice(dotIdx + 1);

      const result = this.followToInteraction(this.instanceId, nodeId, endpointId);
      if (result) interactions.push(result);
    }
    return interactions;
  }

  /** Walk the graph to find a node implementing getInteraction2D(). */
  private followToInteraction(instancePath: string, nodeId: string, endpointId: string, depth = 0): Interaction2D | null {
    if (depth > 10) return null;

    const key = this.graphIndex.nodeKey(instancePath, nodeId);
    const gn = this.graphIndex.getNodeByKey(key);

    // Duck-type check if node exists in the index (primitive nodes)
    if (gn && typeof (gn.node as any).getInteraction2D === 'function') {
      return (gn.node as any).getInteraction2D();
    }

    // Follow vertical reverse edges (subgraph boundary) for this endpoint.
    // Definition instance nodes aren't registered as GraphNodes, but their
    // subgraph boundary edges ARE in the reverse map — so we can still follow them.
    const reverseEdges = this.graphIndex.getReverseEdges(key);
    for (const edge of reverseEdges) {
      if (edge.source !== 'vertical') continue;
      if (edge.targetEndpointId !== endpointId) continue;

      const sourceGn = this.graphIndex.getNodeByKey(edge.sourceKey);
      if (!sourceGn) continue;

      if (typeof (sourceGn.node as any).getInteraction2D === 'function') {
        return (sourceGn.node as any).getInteraction2D();
      }

      // Recurse deeper
      return this.followToInteraction(sourceGn.instancePath, sourceGn.nodeId, edge.sourceEndpointId, depth + 1);
    }

    return null;
  }
}
